import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../UI/Button";
import Card from "../UI/Card";
import SupplierFormModal from "./SupplierFormModal";
import { useTranslation } from "react-i18next";


import {
  getSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from "../api/suppliersApi";

import { getCities } from "../api/citiesApi";

const emptyForm = {
  name: "",
  contacts: "",
  comment: "",
  certificates: "",
  delivery_terms: "",
  warehouses: "",
  city: "",
};

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [cities, setCities] = useState([]);

  const [search, setSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [cityOpen, setCityOpen] = useState(false);

  const navigate = useNavigate();

  const { t } = useTranslation("SuppliersPage");

  /* ================= LOAD ================= */
  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const data = await getSuppliers();
      setSuppliers(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    const data = await getCities();
    setCities(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    loadSuppliers();
    loadCities();
  }, []);

  /* ================= FILTER ================= */
  const filtered = useMemo(() => {
    if (!search) return suppliers;
    return suppliers.filter((s) =>
      (s.name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [search, suppliers]);

  /* ================= OPEN CREATE ================= */
  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setCitySearch("");
    setModalOpen(true);
  };

  /* ================= OPEN EDIT ================= */
  const openEdit = (s) => {
    setEditId(s.id);

    setForm({
      name: s.name || "",
      contacts: s.contacts || "",
      comment: s.comment || "",
      certificates: s.certificates || "",
      delivery_terms: s.delivery_terms || "",
      warehouses: s.warehouses || "",
      city: typeof s.city === "object" ? s.city.id : s.city || "",
    });

    setCitySearch(typeof s.city === "object" ? s.city.name : "");
    setModalOpen(true);
  };

  /* ================= SELECT CITY ================= */
  const selectCity = (city) => {
    setForm((prev) => ({
      ...prev,
      city: city.id,
    }));
    setCitySearch(city.name);
    setCityOpen(false);
  };

  /* ================= DELETE ================= */
  const deleteItem = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await deleteSupplier(id);
    loadSuppliers();
  };

  /* ================= SAVE ================= */
  const save = async () => {
    const payload = {
      name: form.name,
      contacts: form.contacts,
      comment: form.comment,
      certificates: form.certificates,
      delivery_terms: form.delivery_terms,
      warehouses: form.warehouses,
      city_id: form.city || null,
    };

    const res = editId
      ? await updateSupplier(editId, payload)
      : await createSupplier(payload);

    const data = await res.json();

    if (!res.ok) {
      console.error(data);
      alert("Ошибка сохранения");
      return;
    }

    setModalOpen(false);
    setForm(emptyForm);
    setEditId(null);
    setCitySearch("");
    loadSuppliers();
  };

  /* ================= CITY NAME ================= */
  const getCityName = (city) => {
    if (!city) return "-";
    const found = cities.find((c) => String(c.id) === String(city));
    return found ? found.name : city;
  };

  /* ================= UI ================= */
  return (
    <div className="w-full max-w-4xl mx-auto mt-10">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">{t("suppliers")}</h2>

        <Button onClick={openCreate} variant="primary">
          {t("add")}
        </Button>
      </div>

      {/* SEARCH */}
      <input
        className="w-full border p-2 rounded-xl mb-4"
        placeholder={t("search_supplier")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* LIST */}
      <div className="space-y-4">
        {loading ? (
          <p>{t("loading")}</p>
        ) : (
          filtered.map((s) => (
            <Card
              key={s.id}
              hoverable
              className="flex justify-between items-start"
              onClick={() => navigate(`/suppliers/${s.id}`)}
            >
              {/* LEFT */}
              <div className="space-y-1">
                <p className="font-semibold text-lg">{s.name}</p>

                <p> <span className="font-medium">{t("city")}: </span> {getCityName(s.city)}</p>
                <p><span className="font-medium">{t("warehouse")}: </span>{s.warehouses || "-"}</p>
                <p><span className="font-medium">{t("payment_terms")}: </span>{s.delivery_terms || "-"}</p>
                <p><span className="font-medium">{t("certificates")}: </span>{s.certificates || "-"}</p>
                <p><span className="font-medium">{t("contacts")}: </span>{s.contacts || "-"}</p>
                <p className="text-gray-500"><span className="font-medium">{t("comment")}: </span>{s.comment || "-"}</p>
              </div>

              {/* ACTIONS */}
              <div className="flex gap-2">
                <Button
                  variant="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(s);
                  }}
                >
                  {t("edit")}
                </Button>

                <Button
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteItem(s.id);
                  }}
                >
                  {t("delete")}
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* MODAL */}
      <SupplierFormModal
        modalOpen={modalOpen}
        editId={editId}
        form={form}
        setForm={setForm}
        citySearch={citySearch}
        setCitySearch={setCitySearch}
        filteredCities={cities}
        selectCity={selectCity}
        save={save}
        closeModal={() => setModalOpen(false)}
        cityOpen={cityOpen}
        setCityOpen={setCityOpen}
      />
    </div>
  );
};

export default SuppliersPage;